import type { SelectChangeEvent } from '@mui/material';
import SendEmailModal, { EmailType } from '@shared/components/SendEmailModal';
import { ReviewStatus } from '@shared/domain/review';
import { useResponsive } from '@shared/hooks/useResponsive';
import { useApi } from '@shared/services/api';
import { log } from '@shared/utils/log';
import { AnimatePresence } from 'framer-motion';
import React, { useContext, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { UserContext } from '@/shared/contexts/UserContext';
import { useMessageService } from '@/shared/services/MessageService/MessageServiceContext';
import { useReviewContext } from '../../review/context/ReviewContext';
import { ReviewBannerDesktop } from './ReviewBannerDesktop';
import { ReviewBannerMobile } from './ReviewBannerMobile';
import { useReviewBannerActions } from './useReviewBannerActions';

interface BannerProps {
  companyName: string;
  companyUrl?: string;
  reviewDate: string;
  sections: { key: string; title: string }[];
  setIsEditMode: (isEditMode: boolean) => void;
  isEditMode: boolean;
  onScrollToSection: (id: string) => void;
  companyId?: number;
  reviewId?: number;
  reactionCounts?: { likeDislikeCount: number; likePercentage: number };
  isSticky?: boolean;
  onAskQuestionReady?: (handler: () => void) => void;
  refetchQuestions?: () => void;
}

const ReviewBanner = React.forwardRef<HTMLDivElement, BannerProps>(
  (
    {
      companyName,
      companyUrl,
      reviewDate: _reviewDate,
      sections,
      onScrollToSection,
      reviewId = 0,
      setIsEditMode,
      companyId,
      isEditMode,
      reactionCounts,
      isSticky = false,
      onAskQuestionReady,
      refetchQuestions,
    },
    ref,
  ) => {
    const navigate = useNavigate();
    const [selectedSection, setSelectedSection] = useState('');
    const [openSendEmailModal, setOpenSendEmailModal] = useState(false);
    const [isFavorite, setIsFavorite] = useState(false);
    const [isTogglingFavorite, setIsTogglingFavorite] = useState(false);

    const { isMobile } = useResponsive();
    const { user } = useContext(UserContext);
    const { currentReview } = useReviewContext();
    const { openMessage } = useMessageService();
    const api = useApi();

    React.useEffect(() => {
      if (currentReview?.isFavorite !== undefined) {
        setIsFavorite(currentReview.isFavorite);
      }
    }, [currentReview?.isFavorite]);

    const { handleAskQuestionClick, handleEngageAuthorClick } = useReviewBannerActions({
      reviewId,
      companyName,
      companyId,
      refetchQuestions,
    });

    React.useEffect(() => {
      if (onAskQuestionReady) {
        onAskQuestionReady(handleAskQuestionClick);
      }
    }, [onAskQuestionReady, handleAskQuestionClick]);

    const navToCompanyProfile = () => navigate(`/company/${encodeURIComponent(companyName!)}`);

    const handleSectionChange = (event: SelectChangeEvent<string>) => {
      const selectedId = event.target.value;
      setSelectedSection(selectedId);
      onScrollToSection(selectedId);
    };

    const handleToggleEditMode = () => setIsEditMode(!isEditMode);
    const handleShareDealRetro = () => setOpenSendEmailModal(true);

    const isOwnReview = user?.id === currentReview?.userId;
    const editableStatuses = [ReviewStatus.IN_PROGRESS, ReviewStatus.NEEDS_EDITING];
    const canEditReview =
      !!user?.isAdmin || (isOwnReview && editableStatuses.includes(currentReview?.status as ReviewStatus));

    const handleFavoriteToggle = async () => {
      if (!user || !reviewId || isTogglingFavorite) {
        return;
      }

      setIsTogglingFavorite(true);
      try {
        if (isFavorite) {
          await api.delete(`/review/${reviewId}/unfavorite`);
          setIsFavorite(false);
        } else {
          await api.post(`/review/${reviewId}/favorite`);
          setIsFavorite(true);
        }
      } catch (error) {
        log.error('Error toggling favorite:', error);
        openMessage({
          header: 'Error',
          message: 'Failed to update favorite status. Please try again.',
          type: 'error',
        });
      } finally {
        setIsTogglingFavorite(false);
      }
    };

    const isStickyDesktop = isSticky && !isMobile;

    return (
      <>
        <div ref={ref} style={isStickyDesktop ? { visibility: 'hidden' } : undefined}>
          {isMobile ? (
            <ReviewBannerMobile
              companyName={companyName}
              currentReview={currentReview}
              sections={sections}
              selectedSection={selectedSection}
              isEditMode={isEditMode}
              isAdmin={!!user?.isAdmin}
              isOwnReview={isOwnReview}
              canEditReview={canEditReview}
              onSectionChange={handleSectionChange}
              onCompanyClick={navToCompanyProfile}
              onAskQuestion={handleAskQuestionClick}
              onShareDealRetro={handleShareDealRetro}
              onRequestConsultation={handleEngageAuthorClick}
              onToggleEditMode={handleToggleEditMode}
            />
          ) : (
            <ReviewBannerDesktop
              companyId={companyId}
              companyName={companyName}
              companyUrl={companyUrl}
              currentReview={currentReview}
              sections={sections}
              isSticky={false}
              isEditMode={isEditMode}
              isAdmin={!!user?.isAdmin}
              isOwnReview={isOwnReview}
              canEditReview={canEditReview}
              reactionCounts={reactionCounts}
              isFavorite={isFavorite}
              isTogglingFavorite={isTogglingFavorite}
              onCompanyClick={navToCompanyProfile}
              onScrollToSection={onScrollToSection}
              onAskQuestion={handleAskQuestionClick}
              onShareDealRetro={handleShareDealRetro}
              onRequestConsultation={handleEngageAuthorClick}
              onToggleEditMode={handleToggleEditMode}
              onFavoriteToggle={handleFavoriteToggle}
            />
          )}
        </div>

        <AnimatePresence>
          {isStickyDesktop && (
            <ReviewBannerDesktop
              companyId={companyId}
              companyName={companyName}
              companyUrl={companyUrl}
              currentReview={currentReview}
              sections={sections}
              isSticky={true}
              isEditMode={isEditMode}
              isAdmin={!!user?.isAdmin}
              isOwnReview={isOwnReview}
              canEditReview={canEditReview}
              reactionCounts={reactionCounts}
              isFavorite={isFavorite}
              isTogglingFavorite={isTogglingFavorite}
              onCompanyClick={navToCompanyProfile}
              onScrollToSection={onScrollToSection}
              onAskQuestion={handleAskQuestionClick}
              onShareDealRetro={handleShareDealRetro}
              onRequestConsultation={handleEngageAuthorClick}
              onToggleEditMode={handleToggleEditMode}
              onFavoriteToggle={handleFavoriteToggle}
            />
          )}
        </AnimatePresence>

        <SendEmailModal
          isOpen={openSendEmailModal}
          handleClose={() => setOpenSendEmailModal(false)}
          emailType={EmailType.SHARE_REVIEW}
          title="Share Deal Review"
          subtitle={`Share the deal review for ${companyName}`}
          subtext="Send this deal review to your friends or colleagues via email."
          companyName={companyName}
          reviewId={reviewId}
        />
      </>
    );
  },
);

ReviewBanner.displayName = 'ReviewBanner';

export default ReviewBanner;
